import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RegistryContext } from './context';
import type { RegistryClient } from './client';
import type { RegistryActions, RegistryContextValue, RegistryState } from './types';

const EMPTY_STATE: RegistryState = {
  floors: [],
  areas: [],
  devices: [],
  entities: [],
  labels: [],
  ready: false,
};

export interface RegistryProviderProps {
  children: ReactNode;
  client?: RegistryClient;
}

export function RegistryProvider({ children, client }: RegistryProviderProps): JSX.Element {
  const [state, setState] = useState<RegistryState>(EMPTY_STATE);
  const subscribersRef = useRef<Set<() => void>>(new Set());

  const notify = useCallback(() => {
    for (const cb of subscribersRef.current) {
      cb();
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!client) return;
    const next = await client.loadAll();
    setState(next);
    notify();
  }, [client, notify]);

  useEffect(() => {
    if (!client) return;
    let unsubUpdates: (() => void) | undefined;
    let cancelled = false;

    void (async () => {
      try {
        const initial = await client.loadAll();
        if (cancelled) return;
        setState(initial);
        notify();
        unsubUpdates = await client.subscribeUpdates(() => {
          void refresh();
        });
      } catch (err) {
        console.error('RegistryProvider: failed to load registries', err);
      }
    })();

    return () => {
      cancelled = true;
      if (unsubUpdates) unsubUpdates();
    };
  }, [client, refresh, notify]);

  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const actions = useMemo<RegistryActions>(
    () => ({
      refresh: () => {
        void refresh();
      },
      subscribe,
    }),
    [refresh, subscribe],
  );

  const value = useMemo<RegistryContextValue>(() => ({ state, actions }), [state, actions]);

  return <RegistryContext.Provider value={value}>{children}</RegistryContext.Provider>;
}
